import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { marketing } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Loader2, CheckCircle2, ExternalLink, MapPin } from 'lucide-react';

// Public landing page rendered at /l/:slug. Guest-safe. Flow:
//   1. Visitor ticks the opt-in checkbox.
//   2. We immediately record them as a subscriber (anonymous placeholder if
//      no email is on hand) and forward straight to the owner's survey.
//   3. If no survey exists yet, we show a friendly acknowledgment card.
// No email/name form appears — the box tick IS the signup.
export default function LandingPageView() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState(null);
  const submittedRef = useRef(false); // prevent double-submit on rapid re-checks
  const { user } = useAuth();

  const returningEmail = searchParams.get('email') || '';

  useEffect(() => {
    marketing.publicLanding(slug).then(setPage).catch(e => setErr(e?.response?.data?.detail || 'Not found'));
  }, [slug]);

  const optInAndForward = async (checked) => {
    if (!checked || submittedRef.current) return;
    submittedRef.current = true;
    setBusy(true);
    try {
      // If we happen to know the visitor's email (logged in or returning via
      // ?email=), forward it; otherwise the backend creates an anonymous
      // placeholder subscriber automatically.
      const email = (user?.email || returningEmail || '').trim();
      const name = user?.name || '';
      const r = await marketing.optIn(slug, {
        email: email || undefined,
        name: name || undefined,
      });
      if (r?.pointsAwarded > 0) toast({ title: `+${r.pointsAwarded} loyalty pts` });
      // Forward to the owner's survey if one is published. The backend returns
      // the email that got recorded — pass it through so the survey attaches
      // to the same contact (real email or anonymous placeholder).
      try {
        const s = await marketing.latestSurveyForLanding(slug);
        if (s?.slug) {
          const qs = new URLSearchParams({
            email: r?.email || email || '',
            name: name || '',
            from_landing: slug,
            ...(r?.isAnonymous ? { anon: '1' } : {}),
          });
          nav(`/s/${s.slug}?${qs.toString()}`, { replace: true });
          return;
        }
      } catch {
        // No survey — surface the thanks card below.
      }
      setThanks({ ...r, noSurveyYet: true });
    } catch (err2) {
      submittedRef.current = false;
      toast({ title: 'Sign-up failed', description: err2?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  if (err) return <div className="max-w-md mx-auto pbb-card p-8 text-center text-gray-300" data-testid="landing-not-found">{err}</div>;
  if (!page) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const c = page.colors || {};
  return (
    <div className="min-h-screen -m-4" style={{ background: c.bg || '#ffffff', color: c.text || '#111111' }} data-testid="landing-page-view">
      <div className="max-w-3xl mx-auto p-6">
        {page.heroImage && (
          <div className="w-full aspect-[16/9] overflow-hidden rounded-2xl bg-black mb-6">
            <img src={page.heroImage} alt="" className="w-full h-full object-cover"/>
          </div>
        )}
        <div className="flex items-center gap-3 mb-3">
          {page.logo && <img src={page.logo} alt="" className="h-12 object-contain"/>}
          <div>
            <div className="text-2xl md:text-4xl font-black" style={{ color: c.primary || '#16a34a' }} data-testid="landing-view-headline">
              {page.headline}
            </div>
            <div className="text-sm opacity-70">{page.subheadline}</div>
          </div>
        </div>
        {page.business?.city && (
          <div className="text-xs opacity-70 inline-flex items-center gap-1"><MapPin size={11}/> {page.business.city}</div>
        )}
        <p className="mt-4 text-base whitespace-pre-wrap leading-relaxed opacity-90" data-testid="landing-view-body">{page.body}</p>
        {page.ctaText && page.ctaUrl && (
          <a href={page.ctaUrl} target="_blank" rel="noreferrer" data-testid="landing-view-cta"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold"
            style={{ background: c.primary || '#16a34a' }}>
            {page.ctaText} <ExternalLink size={13}/>
          </a>
        )}

        {page.showOptIn && (
          thanks ? (
            <div className="mt-6 rounded-2xl p-5 border" style={{ borderColor: c.primary || '#16a34a', background: (c.primary || '#16a34a') + '10' }} data-testid="landing-thanks">
              <div className="inline-flex items-center gap-2 font-bold" style={{ color: c.primary }}>
                <CheckCircle2 size={16}/> You're on the list!
              </div>
              <p className="text-sm mt-1 opacity-80">
                {thanks.noSurveyYet
                  ? `Thanks for signing up — ${page.business?.name || 'this business'} hasn't published a survey yet, but you'll be first to hear about deals.`
                  : "Look for deals in your inbox soon."}
              </p>
              {thanks.pointsAwarded > 0 && (
                <div className="text-[11px] opacity-70 mt-2">+{thanks.pointsAwarded} loyalty pts added to your PBB profile.</div>
              )}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl p-5 border" data-testid="landing-optin-card"
              style={{ borderColor: c.primary || '#16a34a', background: (c.primary || '#16a34a') + '10' }}>
              <div className="text-sm font-semibold">Get special deals from {page.business?.name || 'us'}</div>
              <label className={`mt-3 flex items-start gap-3 cursor-pointer select-none rounded-lg p-3 transition-colors ${busy ? 'opacity-60' : 'hover:bg-black/5'}`}
                     data-testid="landing-optin-label">
                <input
                  type="checkbox"
                  disabled={busy || submittedRef.current}
                  onChange={e => optInAndForward(e.target.checked)}
                  data-testid="landing-optin-checkbox"
                  className="mt-0.5 h-5 w-5 accent-green-600 shrink-0 cursor-pointer"
                />
                <span className="text-sm">
                  <span className="font-medium">{page.optInLabel || 'Send me deals and offers.'}</span>
                  <span className="block text-xs opacity-70 mt-0.5">
                    {busy
                      ? 'One moment — signing you up…'
                      : "Tick the box and we'll take you straight to a quick survey. No form to fill."}
                  </span>
                </span>
                {busy && <Loader2 size={16} className="animate-spin ml-auto shrink-0" />}
              </label>
            </div>
          )
        )}

        <div className="mt-8 text-[10px] opacity-50 text-center">
          Powered by <Link to="/" className="underline">Promote Black Business</Link>
        </div>
      </div>
    </div>
  );
}
